import { inject, Injectable, signal } from '@angular/core';
import { API_MAP } from 'app/core/helpers/api.map';
import { Result } from 'app/core/models/result';
import { BaseDataService } from 'app/core/services/base-data.service';
import { DoctorSelectionSearchResults } from 'app/shared/components/c66-doctor-selection-result/models/doctor-selection-result.model';
import { DataService } from 'app/shared/services/data.service';
import { Observable, tap, map } from 'rxjs';
import { DoctorSelection } from '../models/doctor-selection.model';
import { ElasticsearchService } from 'app/core/services/elasticsearch.service';
import { LanguageService } from 'app/shared/services/language.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DoctorSelectionService extends BaseDataService<DoctorSelection> {
  override controller = API_MAP.doctorSelection;
  private readonly dataService = inject(DataService);
  private readonly elasticsearchService = inject(ElasticsearchService);
  private readonly currentLanguage = inject(LanguageService).currentLanguage;
  public surgeonDoctorsPerClinic = signal<DoctorSelectionSearchResults[]>([]);

  override get(controller?: string): Observable<Result<any>> {
    return super.get(controller ?? this.controller).pipe(
      tap((d: any) => {
        this.content.set(d.content);
        this.components.set(d.components);
      })
    );
  }

  getSurgeonDoctorsPerClinic(): Observable<DoctorSelectionSearchResults[]> {
    const currentLang = this.currentLanguage();
    const langPrefix = currentLang?.prefix || 'he';
    const indexName = `${langPrefix}_${environment.elasticsearch.indexes.doctor}`;
    
    return this.elasticsearchService
      .initQuery()
      .filterByField('isSurgeon', 'true')
      .addPagination(0, 10000)
      .executeSearch<any>([indexName])
      .pipe(
        map((result) => {
          const doctorsByClinic = this.groupDoctorsByClinic(result.values);
          return doctorsByClinic;
        }),
        tap((s: DoctorSelectionSearchResults[]) => {
          this.surgeonDoctorsPerClinic.set(s);
        })
      );
  }

  private groupDoctorsByClinic(doctors: any[]): DoctorSelectionSearchResults[] {
    const clinicMap = new Map<string, { clinic: any; doctorsList: any[] }>();
    
    const mappedDoctors = doctors.map(esDoctor => ({
      esData: esDoctor,
      doctorData: this.mapElasticsearchDocToDoctor(esDoctor)
    }));

    mappedDoctors.forEach(({ esData }) => {
      const clinics = esData.departments || [];
      
      clinics.forEach((clinic: any) => {
        const clinicKey = clinic.title || clinic.name || clinic.id;

        if (clinicKey && !clinicMap.has(clinicKey)) {
          clinicMap.set(clinicKey, {
            clinic: clinic,
            doctorsList: []
          });
        }
      });
    });

    clinicMap.forEach((clinicData, clinicKey) => {
      mappedDoctors.forEach(({ esData, doctorData }) => {
        const clinics = esData.departments || [];
        
        const isAssociatedWithClinic = clinics.some((c: any) => {
          const cKey = c.title || c.name || c.id;
          return cKey === clinicKey;
        });
        
        if (isAssociatedWithClinic) {
          clinicData.doctorsList.push(doctorData);
        }
      });
    });

    return Array.from(clinicMap.values()).map(({ clinic, doctorsList }) => ({
      clinic: this.mapClinicToLink(clinic),
      doctors: doctorsList
    }));
  }

  private mapClinicToLink(clinic: any): any { 
    return {
      url: clinic.id || clinic.url || clinic.link || '',
      title: clinic.title || clinic.name || '',
    };
  }

  private mapElasticsearchDocToDoctor(esDoc: any): any {
    return {
      contentType: 'doctor',
      doctorName: esDoc.title || '',
      doctorTitle: esDoc.desc || '',
      image: esDoc.photo || {},
      specialty: esDoc.desc || '',
      subSpecialization: '',
      seniorDoctor: esDoc.seniorDoctor || false,
      surgeon: esDoc.isSurgeon || false,
      isSurgeon: esDoc.isSurgeon || false,
      managementMember: false,
      videoLink: {
        url: esDoc.video,
        title: '',
        target: ''
      },
      videoType: '',
      videoDescription: '',
      languages: esDoc.languages,
      linkedinLink: {
        url: esDoc.linkedin
      },
      email: esDoc.email || '',
      blueTicket: esDoc.blueTag || '',
      pinkTicket: esDoc.pinkTag || '',
      askTheDoctorLink: undefined,
      doctorPagePath: esDoc.link || '',
      managerDescription: '',
      displayHonorificTitle: false,
      honorificTitle: '',
      jobType: [],
      gender: esDoc.gender || '',

      id: esDoc.id,
      title: esDoc.title,
      link: esDoc.link,
      typeId: esDoc.typeId,
      typeText: esDoc.typeText
    };
  }
}
